import { useState } from "react";
import { createCourseDraft } from "../service/service";
import { useAuthContext } from "../context/AuthContext";
import Layout from "../components/Layout";
import ConfirmationModal from "../components/ConfirmationModal";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  Snackbar,
  Alert,
  Grid2
} from "@mui/material";
import { useNavigate } from "react-router-dom";

function AddCourse() {
  const { authState, setUserManagedCourses } = useAuthContext();
  const navigate = useNavigate();
  const [courseName, setCourseName] = useState("");
  const [courseDescription, setCourseDescription] = useState("");

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [isSaving, setIsSaving] = useState(false);
  const [courseNameError, setCourseNameError] = useState("");
  const [courseDescriptionError, setCourseDescriptionError] = useState("");

  const validateBeforeSave = () => {
    const errors = [];
    const trimmedCourseName = courseName.trim();
    const trimmedDescription = courseDescription.trim();

    if (!trimmedCourseName) {
      errors.push("Course name is required.");
      setCourseNameError("Course name is required.");
    }

    if (!trimmedDescription) {
      errors.push("Course description is required.");
      setCourseDescriptionError("Course description is required.");
    }

    return errors;
  };

  const buildSubmissionPayload = () => {
    const baseErrors = validateBeforeSave();
    const errors = [...baseErrors];
    if (errors.length) {
      return { errors };
    }

    const payload = {
      course: {
        name: courseName.trim(),
        description: courseDescription.trim(),
      },
    };

    return { payload };
  };

  const handleSaveCourse = async () => {
    setSnackbar({ open: false, message: "", severity: "success" });
    setIsSaving(true);

    const { payload, errors } = buildSubmissionPayload();

    if (errors?.length) {
      setSnackbar({
        open: true,
        severity: "error",
        message: errors[0] || "Failed to prepare course payload.",
      });
      setIsSaving(false);
      return;
    }

    console.log("Prepared course payload for backend submission", payload);

    const response = await createCourseDraft(payload, {
      creatorId: authState.id,
    });

    console.log("Create course draft response:", response);

    if (response.success && response.data?.course) {
      setUserManagedCourses(response.data.course._id);
      setCourseName("");
      setCourseDescription("");
      navigate(`/edit/${response.data.course._id}`); // Navigate to edit page
    }

    setSnackbar({
      open: true,
      severity: response.success ? "success" : "error",
      message: response.success
        ? "Course saved"
        : response?.message || "Failed to create course draft",
    });
    setIsSaving(false);
  };

  const handleCloseSnackbar = (_event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  return (
    <Layout>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "flex-start",
          height: "100%",
          width: "100%",
          backgroundColor: "background.default",
          p: 4,
        }}
      >
        <Paper elevation={0} sx={{ padding: "2rem", width: "100%" }}>
          <Typography variant="h5" gutterBottom>
            Create New Course
          </Typography>

          <Paper sx={{ mt: 2, mb: 3, p: 3, border: "1px solid silver" }}>
            <Grid2 container spacing={2}>
              <Grid2 size={12}>
                <Typography sx={{ mt: 1, mb: 2 }} variant="h6">
                  Course Details
                </Typography>
              </Grid2>

              <Grid2 size={{ xs: 12, md: 4 }}>
                <TextField
                  label="Course Name"
                  variant="outlined"
                  fullWidth
                  value={courseName}
                  onChange={(event) => {
                    setCourseName(event.target.value);
                    if (courseNameError) {
                      setCourseNameError("");
                    }
                  }}
                  error={Boolean(courseNameError)}
                  helperText={courseNameError || undefined}
                />
              </Grid2>

              <Grid2 size={{ xs: 12, md: 8 }}>
                <TextField
                  label="Course Description"
                  variant="outlined"
                  fullWidth
                  value={courseDescription}
                  onChange={(event) => {
                    setCourseDescription(event.target.value);
                    if (courseDescriptionError) {
                      setCourseDescriptionError("");
                    }
                  }}
                  error={Boolean(courseDescriptionError)}
                  helperText={courseDescriptionError || undefined}
                />
              </Grid2>
            </Grid2>
          </Paper>

          <Box sx={{ display: "flex", justifyContent: "center" }}>
            <ConfirmationModal
              title="Confirm new course"
              description={`Course: ${courseName || "(unnamed)"}`}
              helperText="This will create a new course draft. You can add units and levels later."
              buttonLabel={isSaving ? "Preparing..." : "Review & Save Course"}
              loadingLabel="Preparing..."
              confirmLabel="Confirm"
              cancelLabel="Cancel"
              disabled={isSaving}
              loading={isSaving}
              renderTrigger={({ openModal, disabled }) => (
                <Button
                  variant="contained"
                  color="primary"
                  disabled={disabled}
                  onClick={openModal}
                >
                  {isSaving ? "Preparing..." : "Save Course"}
                </Button>
              )}
              onConfirm={handleSaveCourse}
            />
          </Box>

          <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "center", mt: 2 }}
          >
            Course will be saved with name and description only.
          </Typography>
        </Paper>

        <Snackbar
          open={snackbar.open}
          autoHideDuration={4000}
          onClose={handleCloseSnackbar}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseSnackbar}
            severity={snackbar.severity}
            sx={{ width: "100%" }}
          >
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Layout>
  );
}

export default AddCourse;
